import { LayoutSectionKey, LayoutSectionParentKey, QuestionSource } from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import type { inferProcedureOutput } from "@trpc/server";
import { LayoutFormSection } from "components/layout/layout-form-section";
import { NgGrid } from "components/shared/grid";
import { InlineBanner } from "components/ui/inline-banner";
import { Spinner } from "components/ui/spinner";
import { useGetLayoutContainerByKeyQuery } from "generated/graphql";
import React from "react";
import { stableQueryOptions } from "util/requests";

type HomeCareLeadDetail = inferProcedureOutput<AppRouter["homeCareLead"]["get"]>;

const INTAKE_SECTION_KEYS: LayoutSectionKey[] = [
    LayoutSectionKey.HomeCareLeadIntakePayer,
    LayoutSectionKey.HomeCareLeadIntakeSituation,
    LayoutSectionKey.HomeCareLeadIntakeServiceAsk,
];

export const HomeCareLeadIntakeTab: React.FC<{
    client: HomeCareLeadDetail;
    onAnswerCommitted: () => void;
}> = ({ client, onAnswerCommitted }) => {
    const getLayoutContainer = useGetLayoutContainerByKeyQuery(
        { key: LayoutSectionParentKey.HomeCareLeadDetails },
        stableQueryOptions(),
    );

    if (getLayoutContainer.isLoading) {
        return <Spinner />;
    }

    const sections =
        getLayoutContainer.data?.getLayoutContainerByKey?.layoutSections.filter(s =>
            INTAKE_SECTION_KEYS.includes(s.sectionKey as LayoutSectionKey),
        ) ?? [];

    if (sections.length === 0) {
        return (
            <InlineBanner type="info" fontStyle="medium">
                Intake fields are not yet set up for this tenant. An administrator can run
                Ensure Home Care Setup from Tenant Settings to seed them.
            </InlineBanner>
        );
    }

    return (
        <NgGrid>
            {sections.map(section => (
                <NgGrid.Column key={section.id}>
                    <LayoutFormSection
                        entityId={client.id}
                        entity={client}
                        entityType="HomeCareLead"
                        layoutSection={section}
                        mode="tile"
                        answerMode={QuestionSource.Entity}
                        onAnswerCommitted={onAnswerCommitted}
                    />
                </NgGrid.Column>
            ))}
        </NgGrid>
    );
};
